import React, { useState, useEffect } from 'react';
import { me, updateProfile, deactivateAccount, exportMyData } from '../api/auth';

const SECTION = ({ children }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, color: '#9B5FE3',
    borderBottom: '2px solid #e9d8fd',
    paddingBottom: 6, marginTop: 28, marginBottom: 14,
    letterSpacing: '0.06em',
  }}>
    {children}
  </div>
);

const Field = ({ label, required, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <label style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>
      {label}
      {required && <span style={{ color: '#e53e3e', fontSize: 11, marginLeft: 4 }}>必須</span>}
      {!required && <span style={{ color: '#bbb', fontSize: 11, marginLeft: 4 }}>任意</span>}
    </label>
    {children}
  </div>
);

const ReadValue = ({ value }) => (
  <div style={{
    fontSize: 14, color: value ? '#222' : '#bbb',
    padding: '8px 12px',
    background: '#fafafa',
    borderRadius: 8,
    border: '1px solid #eee',
    minHeight: 38,
  }}>
    {value || '—'}
  </div>
);

export default function ProfilePage({ user: initialUser, onUserUpdate }) {
  const [user, setUser]       = useState(initialUser);
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // 最新のユーザー情報を取得
  useEffect(() => {
    me().then(setUser).catch(() => {});
  }, []);

  const startEdit = () => {
    setForm({
      full_name:         user.full_name         ?? '',
      full_name_kana:    user.full_name_kana     ?? '',
      phone:             user.phone             ?? '',
      postal_code:       user.postal_code       ?? '',
      address:           user.address           ?? '',
      company_name:      user.company_name      ?? '',
      company_name_kana: user.company_name_kana ?? '',
    });
    setError('');
    setSuccess('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError('');
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.full_name)       { setError('氏名を入力してください'); return; }
    if (!form.full_name_kana)  { setError('氏名（フリガナ）を入力してください'); return; }
    if (!form.phone)           { setError('電話番号を入力してください'); return; }
    if (!form.postal_code)     { setError('郵便番号を入力してください'); return; }
    if (!form.address)         { setError('住所を入力してください'); return; }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = { ...form };
      if (!payload.company_name)      delete payload.company_name;
      if (!payload.company_name_kana) delete payload.company_name_kana;
      const updated = await updateProfile(payload);
      setUser(updated);
      onUserUpdate?.(updated);
      setEditing(false);
      setSuccess('プロフィールを更新しました');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg).join(' / '));
      } else {
        setError(detail ?? '更新に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 12px', fontSize: 14,
    border: '1.5px solid #d0c0f0', borderRadius: 8,
    outline: 'none', background: '#fff',
    fontFamily: 'inherit',
  };

  return (
    <div style={{
      height: '100%', overflowY: 'auto',
      padding: '32px 40px 48px',
      background: '#f8f7ff',
    }}>
      <div style={{ maxWidth: 640 }}>

        {/* ヘッダー */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#333', margin: 0 }}>
              👤 プロフィール
            </h1>
            <p style={{ fontSize: 13, color: '#888', margin: '6px 0 0' }}>
              登録情報の確認・変更ができます
            </p>
          </div>
          {!editing && (
            <button
              onClick={startEdit}
              style={{
                padding: '10px 22px', borderRadius: 12, border: 'none',
                background: 'linear-gradient(135deg, #9B5FE3, #7048D4)',
                color: '#fff', fontSize: 13, fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(112,72,212,0.3)',
              }}
            >
              ✏️ 編集
            </button>
          )}
        </div>

        {success && (
          <div className="msg" style={{
            background: '#e6ffed', color: '#276749',
            border: '1px solid #9ae6b4', borderRadius: 10,
            padding: '10px 16px', fontSize: 13, marginTop: 12,
          }}>
            ✅ {success}
          </div>
        )}

        {/* カード */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: '28px 32px 36px',
          boxShadow: '0 4px 24px rgba(112,72,212,0.08)',
          marginTop: 20,
        }}>
          {editing ? (
            /* ── 編集フォーム ── */
            <form onSubmit={handleSave}>
              <SECTION>ログイン情報</SECTION>
              <Field label="メールアドレス">
                <ReadValue value={user.email} />
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                  ※ メールアドレスは変更できません
                </div>
              </Field>

              <SECTION>お客様情報</SECTION>
              <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <Field label="氏名" required>
                  <input className="form-input" style={inputStyle}
                    value={form.full_name} onChange={set('full_name')}
                    placeholder="山田 太郎" />
                </Field>
                <Field label="氏名（フリガナ）" required>
                  <input className="form-input" style={inputStyle}
                    value={form.full_name_kana} onChange={set('full_name_kana')}
                    placeholder="ヤマダ タロウ" />
                </Field>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Field label="電話番号" required>
                  <input className="form-input" style={inputStyle}
                    type="tel"
                    value={form.phone} onChange={set('phone')}
                    placeholder="09012345678" />
                </Field>
              </div>

              <div className="grid-2-label" style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 16, marginBottom: 16 }}>
                <Field label="郵便番号" required>
                  <input className="form-input" style={inputStyle}
                    value={form.postal_code} onChange={set('postal_code')}
                    placeholder="1234567" />
                </Field>
                <Field label="住所" required>
                  <input className="form-input" style={inputStyle}
                    value={form.address} onChange={set('address')}
                    placeholder="東京都渋谷区..." />
                </Field>
              </div>

              <SECTION>法人情報（任意）</SECTION>
              <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <Field label="法人名">
                  <input className="form-input" style={inputStyle}
                    value={form.company_name} onChange={set('company_name')}
                    placeholder="株式会社〇〇" />
                </Field>
                <Field label="法人名（フリガナ）">
                  <input className="form-input" style={inputStyle}
                    value={form.company_name_kana} onChange={set('company_name_kana')}
                    placeholder="カブシキガイシャ〇〇" />
                </Field>
              </div>

              {error && (
                <div className="msg msg-error" style={{ marginTop: 20 }}>{error}</div>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ flex: 1, padding: '12px' }}
                >
                  {loading ? <><span className="spinner" />保存中…</> : '💾 保存する'}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={loading}
                  style={{
                    flex: 1, padding: '12px',
                    borderRadius: 12, border: '1.5px solid #d0c0f0',
                    background: '#fff', color: '#7048D4',
                    fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  キャンセル
                </button>
              </div>
            </form>
          ) : (
            /* ── 閲覧モード ── */
            <>
              <SECTION>ログイン情報</SECTION>
              <div style={{ display: 'grid', gap: 12, marginBottom: 4 }}>
                <Field label="メールアドレス">
                  <ReadValue value={user.email} />
                </Field>
              </div>

              <SECTION>お客様情報</SECTION>
              <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <Field label="氏名">
                  <ReadValue value={user.full_name} />
                </Field>
                <Field label="氏名（フリガナ）">
                  <ReadValue value={user.full_name_kana} />
                </Field>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Field label="電話番号">
                  <ReadValue value={user.phone} />
                </Field>
              </div>
              <div className="grid-2-label" style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 16 }}>
                <Field label="郵便番号">
                  <ReadValue value={user.postal_code} />
                </Field>
                <Field label="住所">
                  <ReadValue value={user.address} />
                </Field>
              </div>

              <SECTION>法人情報</SECTION>
              <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <Field label="法人名">
                  <ReadValue value={user.company_name} />
                </Field>
                <Field label="法人名（フリガナ）">
                  <ReadValue value={user.company_name_kana} />
                </Field>
              </div>

              {(!user.full_name || !user.phone || !user.address) && (
                <div style={{
                  marginTop: 24, padding: '12px 16px',
                  background: '#fffbeb', border: '1px solid #fbd38d',
                  borderRadius: 10, fontSize: 13, color: '#744210',
                }}>
                  ⚠️ 未入力の必須項目があります。「編集」から情報を登録してください。
                </div>
              )}
            </>
          )}
        </div>

        {/* セキュリティセクション */}
        {!editing && (
          <div style={{
            background: '#fff', borderRadius: 20,
            padding: '24px 32px', marginTop: 20,
            boxShadow: '0 4px 24px rgba(112,72,212,0.08)',
          }}>
            <SECTION>セキュリティ</SECTION>
            <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
              <div style={{
                padding: '14px 16px', borderRadius: 12,
                background: user.totp_enabled ? '#e6ffed' : '#fff5f5',
                border: `1px solid ${user.totp_enabled ? '#9ae6b4' : '#fed7d7'}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 4 }}>
                  二段階認証（2FA）
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: user.totp_enabled ? '#276749' : '#c53030' }}>
                  {user.totp_enabled ? '🔐 有効' : '⚠️ 無効'}
                </div>
              </div>
              <div style={{
                padding: '14px 16px', borderRadius: 12,
                background: '#f0f4ff', border: '1px solid #c3d8ff',
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 4 }}>
                  アカウント権限
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#2b4acb' }}>
                  {user.role === 'admin' ? '🛠️ 管理者' : '👤 一般ユーザー'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 個人データダウンロード（GDPR/個情法対応） */}
        {!editing && <ExportDataSection />}

        {/* 退会セクション（管理者以外） */}
        {!editing && user.role !== 'admin' && (
          <DeactivateSection user={user} />
        )}

      </div>
    </div>
  );
}


function DeactivateSection({ user }) {
  const [open, setOpen]         = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [purge, setPurge]       = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!password)                  { setError('パスワードを入力してください'); return; }
    if (confirm.trim() !== '退会する') { setError('確認テキストに「退会する」と入力してください'); return; }
    const confirmMsg = purge
      ? '本当に退会＋データを即時完全削除しますか？\n\n・登録された顔データはすべて削除されます\n・サブスクリプションは即時キャンセルされます\n・すべてのデータが今すぐ完全削除されます（復元不可）'
      : '本当に退会しますか？\n\n・登録された顔データはすべて削除されます\n・サブスクリプションは即時キャンセルされます\n・データは30日後に完全削除されます';
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    setError('');
    try {
      await deactivateAccount({ password, confirm_text: confirm.trim(), purge_immediately: purge });
      alert('退会が完了しました。ご利用ありがとうございました。');
      // 認証 Cookie はサーバー側で消去済み
      window.location.hash = '';
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.detail ?? '退会処理に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: '#fff', borderRadius: 20,
      padding: '24px 32px', marginTop: 20,
      boxShadow: '0 4px 24px rgba(112,72,212,0.08)',
      border: '1px solid #fed7d7',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#c53030', borderBottom: '2px solid #fed7d7', paddingBottom: 6, marginBottom: 14, letterSpacing: '0.06em' }}>
        退会（アカウント削除）
      </div>

      {!open ? (
        <>
          <p style={{ fontSize: 13, color: '#666', lineHeight: 1.7, margin: '0 0 14px' }}>
            退会するとサブスクリプションは即時キャンセルされ、登録された顔データはすべて削除されます。30日後にアカウント情報も完全削除されます。
          </p>
          <div style={{
            fontSize: 12, color: '#744210', background: '#fffbeb',
            border: '1px solid #fbd38d', borderRadius: 10,
            padding: '10px 14px', marginBottom: 14, lineHeight: 1.7,
          }}>
            💡 <strong>サブスクリプション契約中の方へ：</strong><br />
            退会するとサブスクが<strong>即時</strong>キャンセルされ、契約期間が残っていても使えなくなります。
            <strong>期間末まで使い続けたい場合は、先に「料金管理」ページから解約予約してから退会してください</strong>。
          </div>
          <button
            onClick={() => setOpen(true)}
            style={{
              padding: '10px 22px', borderRadius: 12, border: '1.5px solid #fc8181',
              background: '#fff', color: '#c53030',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            退会の手続きに進む
          </button>
        </>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: '#c53030', background: '#fff5f5', padding: '12px 14px', borderRadius: 10, lineHeight: 1.6 }}>
            ⚠️ 退会後、データの復旧はできません。
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#555', display: 'block', marginBottom: 4 }}>
              現在のパスワード
            </label>
            <input
              type="password" className="form-input"
              value={password} onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 12px', fontSize: 14,
                border: '1.5px solid #d0c0f0', borderRadius: 8,
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#555', display: 'block', marginBottom: 4 }}>
              確認のため「退会する」と入力してください
            </label>
            <input
              type="text" className="form-input"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              placeholder="退会する"
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '8px 12px', fontSize: 14,
                border: '1.5px solid #d0c0f0', borderRadius: 8,
              }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#742a2a', lineHeight: 1.6, cursor: 'pointer', padding: '10px 12px', background: '#fff5f5', borderRadius: 8, border: '1px dashed #fc8181' }}>
            <input
              type="checkbox"
              checked={purge}
              onChange={(e) => setPurge(e.target.checked)}
              style={{ marginTop: 3, flexShrink: 0 }}
            />
            <span>
              <strong>データを即時完全削除する</strong>（30日待たずに今すぐ削除）<br />
              <span style={{ fontSize: 11, color: '#999' }}>
                個人情報保護法・GDPR の「忘れられる権利」対応。一度削除すると復元不可能です。
              </span>
            </span>
          </label>
          {error && <div className="msg msg-error">{error}</div>}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="submit" disabled={loading}
              style={{
                flex: 1, padding: '12px', borderRadius: 12, border: 'none',
                background: '#e53e3e', color: '#fff',
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {loading ? '処理中…' : (purge ? '退会＋完全削除を確定する' : '退会を確定する')}
            </button>
            <button
              type="button" onClick={() => { setOpen(false); setPassword(''); setConfirm(''); setPurge(false); setError(''); }}
              disabled={loading}
              style={{
                flex: 1, padding: '12px', borderRadius: 12, border: '1.5px solid #d0c0f0',
                background: '#fff', color: '#7048D4',
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              キャンセル
            </button>
          </div>
        </form>
      )}
    </div>
  );
}


function ExportDataSection() {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleExport = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `egao-folder-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail ?? 'データ取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: '#fff', borderRadius: 20,
      padding: '24px 32px', marginTop: 20,
      boxShadow: '0 4px 24px rgba(112,72,212,0.08)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9B5FE3', borderBottom: '2px solid #e9d8fd', paddingBottom: 6, marginBottom: 14, letterSpacing: '0.06em' }}>
        個人データのダウンロード
      </div>
      <p style={{ fontSize: 13, color: '#666', lineHeight: 1.7, margin: '0 0 14px' }}>
        登録情報・お子様の情報・振り分け履歴を JSON 形式でダウンロードできます（個人情報保護法・GDPR データポータビリティ対応）。
      </p>
      {error && <div className="msg msg-error" style={{ marginBottom: 12 }}>{error}</div>}
      <button
        onClick={handleExport}
        disabled={loading}
        style={{
          padding: '10px 22px', borderRadius: 12, border: '1.5px solid #d0c0f0',
          background: '#fff', color: '#7048D4',
          fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}
      >
        {loading ? '取得中…' : '📥 データをダウンロード (JSON)'}
      </button>
    </div>
  );
}
